import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { FeatureCollection } from 'geojson';
import mapboxgl, { GeoJSONSource, LngLatLike } from 'mapbox-gl';
import { useCallback, useEffect } from 'react';
/* eslint-disable import/no-webpack-loader-syntax */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import MapboxWorker from 'worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker';
/* eslint-enable import/no-webpack-loader-syntax */
import { getHubConfig } from '../../../../const/HubConfig';
import { settings } from '../../../../settings';
import { Region } from '../../../../types/orderTypes';
import Fetch from '../../../../utils/fetch';
import { mapboxApiKey } from './../../../../fetch/mapboxApi';

mapboxgl.accessToken = mapboxApiKey;
(mapboxgl as any).workerClass = MapboxWorker;

const areaOverlayId = 'area-overlay';
const overlayLayerId = 'overlay';
const overlayBorderLayerId = 'overlay-border';
const NORWAY_INITIAL_LOCATION: LngLatLike = [14.212085, 65.2628761];

type MapboxMapOptions = {
  draw?: React.MutableRefObject<MapboxDraw>;
  fitToAreaOverlayBounds?: boolean;
};

/**
 * TODO: The region is unset at some point,
 * Try reloading the page while on /planner url
 */
export const useMapboxMap = (
  map: React.MutableRefObject<mapboxgl.Map>,
  mapContainer: React.MutableRefObject<undefined>,
  region: Region,
  options: MapboxMapOptions | undefined = {
    fitToAreaOverlayBounds: true,
  },
  initialPosition?: LngLatLike | undefined
): (() => Promise<void>) => {
  const addHubPositions = useCallback(() => {
    const hubConfig = getHubConfig(region);
    const hubPositions = [
      hubConfig.hubAddress.location,
      ...hubConfig.handoverPointAddresses.map(address => address.location),
    ];

    hubPositions.forEach(hubPosition => {
      const el = document.createElement('div');
      el.className = 'marker';
      el.style.backgroundImage = `url("data:image/svg+xml;utf8,<svg width='100%' height='100%' viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'> <path d='M166.064,0C184.794,0 200,15.206 200,33.936L200,101.807C200,120.536 184.794,135.742 166.064,135.742L135.239,135.742L100,200L64.761,135.742L33.936,135.742C15.206,135.742 0,120.536 0,101.807L0,33.936C0,15.206 15.206,0 33.936,0L166.064,0Z' fill='white' style='stroke-width:4px; stroke:rgba(0,0,0,1)' /> <text y='90px' style='font-family:Arial; font-size:60px; fill:black;' transform='translate(100)'> <tspan x='0' text-anchor='middle'>HUB</tspan> </text></svg>")`;
      el.style.width = `40px`;
      el.style.height = `40px`;
      el.style.backgroundSize = '100%';

      new mapboxgl.Marker(el, { anchor: 'bottom' })
        .setLngLat([hubPosition.longitude, hubPosition.latitude])
        .addTo(map.current);
    });
  }, [map, region]);

  const addAreaOverlay = useCallback(async () => {
    const response = await Fetch.get({
      url: `${settings.newApiUrl}/availability/area-overlay/${region}`,
    });

    if (response.ok) {
      const areaOverlay: FeatureCollection = await response.json();
      if (map.current.getSource(areaOverlayId)) {
        (map.current.getSource(areaOverlayId) as GeoJSONSource).setData(
          areaOverlay
        );
      } else {
        map.current.addSource(areaOverlayId, {
          type: 'geojson',
          data: areaOverlay,
        });
      }
      map.current.getLayer(overlayLayerId) &&
        map.current.removeLayer(overlayLayerId);
      map.current.getLayer(overlayBorderLayerId) &&
        map.current.removeLayer(overlayBorderLayerId);
      map.current.boxZoom.disable();

      map.current.addLayer({
        id: overlayLayerId,
        type: 'fill',
        source: areaOverlayId,
        layout: {},
        paint: {
          'fill-color': 'black',
          'fill-opacity': 0.4,
        },
      });

      map.current.addLayer({
        id: overlayBorderLayerId,
        type: 'line',
        source: areaOverlayId,
        layout: {},
        paint: {
          'line-color': '#661aff',
          'line-width': 2,
        },
      });

      if (options.fitToAreaOverlayBounds) {
        const coordinates = (areaOverlay.features[0].geometry as any)
          .coordinates[0][1];

        if (coordinates) {
          const bounds = new mapboxgl.LngLatBounds(
            coordinates[0],
            coordinates[0]
          );

          coordinates.forEach(coord => {
            bounds.extend(coord);
          });

          map.current.fitBounds(bounds);
        }
      }
    }
  }, [map, options.fitToAreaOverlayBounds, region]);

  useEffect(() => {
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/porterbuddy/ckq0y4l310hcn18npf6yokoz0',
      center: initialPosition || NORWAY_INITIAL_LOCATION,
      zoom: initialPosition ? 10 : 4,
    });

    if (options.draw) {
      options.draw.current = new MapboxDraw({
        displayControlsDefault: false,
        controls: { polygon: true, trash: true },
      });
    }

    map.current.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
    if (options.draw) {
      map.current.addControl(options.draw.current, 'bottom-right');
    }

    map.current.on('load', () => {
      addAreaOverlay();
      addHubPositions();
    });

    return () => {
      map.current.off('load', () => {
        addAreaOverlay();
        addHubPositions();
      });
    };
  }, [
    addAreaOverlay,
    initialPosition,
    addHubPositions,
    map,
    mapContainer,
    options.draw,
  ]);

  return addAreaOverlay;
};
